import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grow,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { SetDeposit } from 'store/actions/schoolDetailActions';
import { FETCH_PACKAGE } from 'store/types/packageTypes';

import UnlockEstimateComponent from 'components/UnlockEstimateComponent';
import CardAdditionalComponent from 'components/SummaryComponent/SummaryContentComponent/CardAdditionalComponent';
import CardAccommodationComponent from 'components/SummaryComponent/SummaryContentComponent/CardAccommodationComponent';
import CardCourseComponent from 'components/SummaryComponent/SummaryContentComponent/CardCourseComponent';
import CardPackageComponent from 'components/SummaryComponent/SummaryContentComponent/CardPackageComponent';
import SummaryTotalValueComponent from 'components/SummaryComponent/SummaryTotalValueComponent';
import StyledTableCell from 'components/SummaryComponent/StyledTableCell';
import SelectGroupComponent from 'components/SelectGroupComponent';
import CustomTotalValueComponent from 'components/SalesExecutivesComponents/CustomTotalValueComponent';
import { calcDepositMinimum } from 'components/CheckoutComponents/CheckoutController/index';

import { shouldUnlockPrice } from 'utils/lead';
import { getSelectedCourses } from 'utils/courseUtils';
import { getSelectedAccommodationGroups } from 'utils/accommodationUtils';
import {
  getLocationInsurances,
  getSelectedInsurances,
} from 'utils/insuranceUtils';
import {
  getSelectedAdditionalServices,
  SumInsurancePrice,
} from 'utils/additionalServicesUtils';
import { getSelectedPackage } from 'utils/packageUtils';
import {
  formatDate,
  getDaysAdded,
  getGroupedMondayOptions,
  getNextBusinessDayInRange,
} from 'utils/date';

function SummaryContentComponent({
  createOrder,
  dateParams,
  isSalesExecutive,
  isFetching,
  open,
  setOpen,
  summary,
}) {
  const { lead } = useSelector((store) => store.studentReducer);
  const { user } = useUser();
  const {
    accommodationGroups,
    additionalServices,
    courses,
    location,
    needAccommodation,
  } = useSelector((store) => store.schoolDetailReducer);
  const { detail } = useSelector((store) => store.packageReducer);

  const dispatch = useDispatch();

  const [boardDate, setBoardDate] = useState(
    formatDate(
      getNextBusinessDayInRange(
        getDaysAdded(new Date(), parseInt(process.env.DAYS_BEFORE_BOOKING, 10))
      ),
      'iso-string'
    )
  );

  const selectedCourses = getSelectedCourses(courses);
  const selectedAccommodationGroups =
    getSelectedAccommodationGroups(accommodationGroups);
  const selectedAdditionalServices = getSelectedAdditionalServices(
    additionalServices,
    dateParams.courseWeeks,
    accommodationGroups.some((ag) => ag.selected)
  );
  const selectedInsurances = getSelectedInsurances(
    getLocationInsurances(location)
  );
  const selectedPackage = getSelectedPackage(detail);

  const focusOnTab = (tabId) => {
    if (typeof window !== 'undefined') {
      const tab = document.getElementById(tabId);
      const scroll = document.getElementById('buildPackage');

      tab.click();

      if (scroll) {
        scroll.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }

    if (open) setOpen(false);
  };

  // Migrar código para fora do sumário
  const schoolMinimum = useSelector(
    (store) => store.schoolDetailReducer.minimumPayment
  );

  const minimumPackage = detail.minimumPayment;
  const minimumPayment = minimumPackage || schoolMinimum;

  let depositMinimum = calcDepositMinimum(
    summary.totalDiscountExchanged,
    new Date(dateParams.courseStart),
    schoolMinimum
  );

  const [deposit, setDeposit] = useState(0);

  // detalhe de pacote
  if (detail.deposit) depositMinimum = detail.deposit.totalExchanged;
  // Migrar código para fora do sumário

  useEffect(() => {
    setDeposit(depositMinimum);
    SetDeposit(dispatch, depositMinimum);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositMinimum]);

  return (
    <Box
      bgcolor="background.secondary.main"
      borderRadius="4px"
      overflow="hidden"
      position="relative"
    >
      {!shouldUnlockPrice(lead, user) && (
        <Box
          height="100%"
          padding={3}
          position="absolute"
          textAlign="center"
          width="100%"
          zIndex="speedDial"
        >
          <Box
            p={1}
            py={3}
            borderRadius="4px"
            border={1}
            borderColor="border.main"
            bgcolor="background.paper"
            position="relative"
            top="10%"
          >
            <UnlockEstimateComponent />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          filter: shouldUnlockPrice(lead, user) ? '' : 'blur(5px)',
          userSelect: shouldUnlockPrice(lead, user) ? 'initial' : 'none',
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            marginTop: '16px',
            textAlign: 'center',
            textTransform: 'uppercase',
          }}
          variant="h5"
          component="h2"
        >
          Resumo da Reserva
        </Typography>

        {selectedCourses.length > 0 && (
          <Box m={2}>
            <Card elevation={3}>
              {selectedCourses.map((course) => (
                <Table aria-label="customized table" key={course.id}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Curso</StyledTableCell>
                      <StyledTableCell align="right">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <IconButton
                            size="small"
                            color="inherit"
                            aria-label="settings"
                            onClick={() => {
                              focusOnTab('courseTab');
                            }}
                          >
                            <Typography variant="body2">Alterar</Typography>
                            &nbsp;
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <CardCourseComponent
                    course={course}
                    dateParams={dateParams}
                    key={course.id}
                  />
                </Table>
              ))}
            </Card>
          </Box>
        )}

        {needAccommodation && (
          <Grow in={!!needAccommodation}>
            <Box mx={2}>
              <Card elevation={3}>
                {selectedAccommodationGroups.map((group) => {
                  // Representa Accommodation
                  const accommodation = group.accommodations.filter(
                    (d) => d.selected === true
                  )[0];

                  const bedroom = accommodation.priceRanges.filter(
                    (d) => d.selected === true
                  )[0];

                  return (
                    <Table aria-label="customized table" key={group.id}>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Acomodação</StyledTableCell>
                          <StyledTableCell align="right">
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                            >
                              <IconButton
                                size="small"
                                color="inherit"
                                aria-label="settings"
                                onClick={() => {
                                  focusOnTab('accommodationTab');
                                }}
                              >
                                <Typography variant="body2">Alterar</Typography>
                                &nbsp;
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <CardAccommodationComponent
                        key={accommodation.id}
                        accommodation={accommodation}
                        bedroom={bedroom.bedroomName}
                        dateParams={dateParams}
                      />
                    </Table>
                  );
                })}
              </Card>
            </Box>
          </Grow>
        )}

        {(selectedAdditionalServices.length > 0 ||
          selectedInsurances.length > 0) && (
          <Box m={2}>
            <Card elevation={3}>
              <Table aria-label="additionals table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Adicionais</StyledTableCell>
                    <StyledTableCell align="right">
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        onClick={() => {
                          focusOnTab('additionalTab');
                        }}
                      >
                        <IconButton
                          size="small"
                          color="inherit"
                          aria-label="settings"
                        >
                          <Typography variant="body2">Alterar</Typography>
                          &nbsp;
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedAdditionalServices.length > 0 &&
                    selectedAdditionalServices.map((additional) => (
                      <CardAdditionalComponent
                        key={additional.id}
                        title={additional.title}
                        value={additional.priceExchanged}
                      />
                    ))}
                  {selectedInsurances.length > 0 &&
                    selectedInsurances?.map((row, index) => (
                      <CardAdditionalComponent
                        key={index}
                        title="Seguro Viagem"
                        value={SumInsurancePrice(
                          [row],
                          dateParams?.courseWeeks
                        )}
                      />
                    ))}
                </TableBody>
              </Table>
            </Card>
          </Box>
        )}

        {selectedPackage && (
          <>
            <Box m={2}>
              <Card elevation={3}>
                <Table aria-label="date table">
                  <TableBody>
                    <TableRow>
                      <StyledTableCell component="th" scope="row">
                        <strong>Escolha a data de início</strong>
                      </StyledTableCell>
                      <StyledTableCell component="th" scope="row">
                        <SelectGroupComponent
                          disableMargin
                          fullWidth
                          groups={getGroupedMondayOptions(detail?.dateUseTo)}
                          id="boardDate"
                          name="date"
                          value={boardDate}
                          change={(date) => {
                            setBoardDate(date);

                            const startDate = new Date(date);

                            dispatch({
                              payload: { ...detail, startDate },
                              type: FETCH_PACKAGE,
                            });
                          }}
                        />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </Box>
            <Box m={2}>
              <Card elevation={3}>
                <Table aria-label="package table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell colSpan={2}>Pacote</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <CardPackageComponent
                    pkg={selectedPackage}
                    startDate={boardDate}
                  />
                </Table>
              </Card>
            </Box>
          </>
        )}

        <Box textAlign="center" m={2}>
          {/* Consolidar todos esses parâmetros no objeto Summary */}
          {!isSalesExecutive ? (
            <SummaryTotalValueComponent
              currency={summary.currency.symbol}
              deposit={depositMinimum}
              showAccommodationFee={needAccommodation}
              summaryItems={summary}
              totalDiscount={summary.totalDiscount}
              totalDiscountExchanged={summary.totalDiscountExchanged}
            />
          ) : (
            <CustomTotalValueComponent
              currency={summary.currency.symbol}
              deposit={deposit}
              header="Depósito do cliente"
              minimumPayment={minimumPayment}
              setDeposit={setDeposit}
              showAccommodationFee={needAccommodation}
              summaryItems={summary}
              totalDiscount={summary.totalDiscount}
              totalDiscountExchanged={summary.totalDiscountExchanged}
            />
          )}
          {/* Consolidar todos esses parâmetros no objeto Summary */}
          <Box mt={1} sx={{ position: 'relative' }}>
            {shouldUnlockPrice(lead, user) && (
              <Button
                color="primary"
                disabled={isFetching}
                fullWidth
                onClick={createOrder}
                variant="contained"
              >
                <Box fontWeight="bold" fontSize={16}>
                  Gerar Orçamento
                </Box>
              </Button>
            )}
            {isFetching && (
              <CircularProgress
                size={24}
                sx={{
                  left: '50%',
                  marginLeft: '-12px',
                  marginTop: '-12px',
                  position: 'absolute',
                  top: '50%',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

SummaryContentComponent.propTypes = {
  summary: PropTypes.instanceOf(Object).isRequired,
  createOrder: PropTypes.instanceOf(Object).isRequired,
  dateParams: PropTypes.instanceOf(Object).isRequired,
  open: PropTypes.bool,
  setOpen: PropTypes.instanceOf(Object),
  isFetching: PropTypes.bool,
  isSalesExecutive: PropTypes.bool,
};

SummaryContentComponent.defaultProps = {
  open: false,
  setOpen: PropTypes.instanceOf(Object),
  isFetching: false,
  isSalesExecutive: false,
};

export default SummaryContentComponent;
